import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { Box, Button, Stack } from '@mui/material'
import debounce from 'lodash-es/debounce'
import { useEffect, useMemo } from 'react'
import { nameOfSquare } from '../../../chess/basics'
import { getMoveById, getPositionById } from '../../../chess/gameTree'
import { Square } from '../../../react-chessboard/src/chessboard/types'
import { Dialogs, useDialogs } from '../../../sharedComponents/src/Contexts/DialogsContext'
import { useGlobalContext } from '../../../sharedComponents/src/Contexts/GlobalContext'
import { GroupNames } from '../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useScreenSize } from '../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import CircularButton from '../../analysisView/components/analyisControls/CircularButton'
import HintLimitDialog from '../../PuzzlesView/components/HintLimitDialog'
import HintIcon from '../../PuzzlesView/icons/Hint'
import SolutionIcon from '../../PuzzlesView/icons/Solution'

export const PuzzleQuestControls = () => {
    const { isMobile } = useScreenSize()
    const stepProgress = useStoreState((state) => state.puzzleQuestView.stepProgress)

    const { userType } = useGlobalContext()
    const { registerDialog } = useDialogs()
    const gameTree = useStoreState((state) => state.puzzleQuestView.gameTree)
    const currentPosition = useStoreState((state) => state.puzzleQuestView.currentPosition)
    const myColor = useStoreState((state) => state.puzzleQuestView.myColor)
    const currentPuzzleNumber = useStoreState((state) => state.puzzleQuestView.currentPuzzleNumber)
    const showSolution = useStoreState((state) => state.puzzleQuestView.showSolution)
    const puzzleState = useStoreState((state) => state.puzzleQuestView.puzzleState)
    const availableHints = useStoreState((state) => state.puzzleQuestView.availableHints)
    const showHintLimit = useStoreState((state) => state.puzzleQuestView.showHintLimit)
    const hint = useStoreState((state) => state.puzzleQuestView.hint)
    const customArrows = useStoreState((state) => state.puzzleQuestView.customArrows)

    const setHintAsync = useStoreActions((actions) => actions.puzzleQuestView.setHintAsync)
    const setShowSolution = useStoreActions((actions) => actions.puzzleQuestView.setShowSolution)
    const setShowingSolution = useStoreActions((actions) => actions.puzzleQuestView.setShowingSolution)
    const setMoveStatus = useStoreActions((actions) => actions.puzzleQuestView.setMoveStatus)
    const nextPuzzle = useStoreState((state) => state.puzzleQuestView.nextPuzzle)
    const savePuzzle = useStoreActions((actions) => actions.puzzleQuestView.savePuzzle)
    const showRightSolution = useStoreActions((actions) => actions.puzzleQuestView.showRightSolution)
    const setShowHintLimit = useStoreActions((actions) => actions.puzzleQuestView.setShowHintLimit)

    const handleHint = async () => {
        if (availableHints === 0) {
            // show hint limit
            if (userType === GroupNames.SUBSCRIBED) {
                setShowHintLimit(true)
            } else {
                registerDialog(Dialogs.HINT_LIMIT, <HintLimitDialog />)
            }
        } else {
            // handle hint
            if (customArrows.length === 0) {
                const currentProgressPosition = getPositionById(gameTree, currentPosition.id)
                const expectedProgressMove = getMoveById(gameTree, currentProgressPosition?.nextMoveIds[0])
                if (expectedProgressMove && currentProgressPosition.position.turn === myColor) {
                    const nextMoveFrom = nameOfSquare(expectedProgressMove?.move.from)
                    if (!hint) {
                        await setHintAsync({ from: nextMoveFrom as Square })
                    } else {
                        const nextMoveTo = nameOfSquare(expectedProgressMove?.move.to)
                        await setHintAsync({ from: nextMoveFrom as Square, to: nextMoveTo as Square })
                    }
                }
            }
        }
    }

    const debouncedHandleHint = useMemo(() => {
        return debounce(
            () => {
                handleHint()
            },
            500,
            { leading: true, trailing: false },
        )
    }, [handleHint])

    useEffect(() => {
        return () => {
            debouncedHandleHint.cancel()
        }
    }, [debouncedHandleHint])

    const handleShowSolution = () => {
        setShowingSolution(true)
        setShowSolution(true)
        setMoveStatus(undefined)
    }

    const handleShowRightSolution = () => {
        showRightSolution()
    }

    const showSolutionButton = puzzleState === false && !showSolution
    const lastShowSolutionButton = showSolutionButton && stepProgress.progress.length === 10

    const showControls = !nextPuzzle && !lastShowSolutionButton
    const showNextButton = nextPuzzle
    const hintDescription =
        userType === GroupNames.SUBSCRIBED && availableHints === 0 ? 'Earn more from Daily Bonus' : undefined

    return (
        <>
            {showControls && (
                <Box
                    sx={{
                        height: '56px',
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexGrow: 0,
                        gap: '24px',
                    }}
                >
                    <CircularButton
                        onClick={handleShowSolution}
                        title="Solution"
                        icon={<SolutionIcon />}
                        disabled={Boolean(nextPuzzle) || showSolution || currentPosition.position.turn !== myColor}
                    />

                    <CircularButton
                        badge={availableHints >= 0 ? availableHints : undefined}
                        onClick={debouncedHandleHint}
                        title="Hint"
                        description={hintDescription}
                        icon={<HintIcon />}
                        disabled={Boolean(nextPuzzle) || currentPosition.position.turn !== myColor}
                        showTooltip={showHintLimit}
                        animated={customArrows.length === 0}
                    />
                </Box>
            )}
            {showNextButton || lastShowSolutionButton ? (
                <Stack
                    direction="row"
                    display="flex"
                    height="56px"
                    alignItems="center"
                    gap={1}
                    sx={{
                        '> button': {
                            px: 0,
                        },
                    }}
                >
                    {showSolutionButton && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleShowRightSolution}
                            fullWidth
                            size={isMobile ? 'medium' : 'large'}
                        >
                            Solution
                        </Button>
                    )}
                    {showNextButton && (
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!nextPuzzle || currentPuzzleNumber >= 10}
                            onClick={() => savePuzzle(nextPuzzle!)}
                            fullWidth
                            size={isMobile ? 'medium' : 'large'}
                            endIcon={<ArrowForwardIcon />}
                        >
                            Next Puzzle
                        </Button>
                    )}
                </Stack>
            ) : (
                <></>
            )}
        </>
    )
}
